import {
  type BotHardwareDataType,
  type BotHardwareEquipmentType,
  type RawArtifactType,
} from './types';

/**
 * Sentinel value embedded in the model directory path to identify the
 * camera-free dummy circle model.  Shared across the TS codebase so
 * that the magic string is defined in exactly one place.
 */
export const DUMMY_MODEL_SENTINEL = '__dummy__';

/**
 * `TrainingDataFile.type` value for the raw Insta360 video (`.insv`) on a
 * handheld recording. `.insv` is exclusive to Insta360 captures, so it's the
 * canonical signal that a session is handheld. Mirrors the
 * `TrainingDataFileType.Insv` GraphQL enum.
 */
export const INSV_FILE_TYPE = 'insv';

/**
 * Auto Reviewer User ID
 * This is a system user used for automated validation and review processes.
 * Defined in migration 0169_create_auto_reviewer_user
 */
export const AUTO_REVIEWER_USER_ID = 'user_0sapi_0X7mK9pQ2vN4rT6wY8zB';

/**
 * Default Recording User ID
 * This is the default user ID used for recordings (including inference recordings)
 * when no user ID is explicitly provided.
 * Used in CreateRecording step and other recording contexts.
 */
export const DEFAULT_RECORDING_USER_ID = 'user_0sapi_saa1nUhOxcvsd436sgf35f';

/**
 * Raw artifacts a hardware `dataType` may deliver at recording time, first
 * entry = default. Only `video` has a real choice: a normal camera uploads an
 * mp4 (raw_rgb_video) while an Insta360 uploads a .insv container.
 * `force_hardware` records no pipeline raw input. Used to default and validate
 * `BotHardware.rawArtifactType` (create resolver + dashboard create modal).
 */
export const DATA_TYPE_TO_RAW_ARTIFACT_TYPES: {
  [key in BotHardwareDataType]: RawArtifactType[];
} = {
  pose: ['pose'],
  joint_states: ['joint_states'],
  joint_desires: ['joint_desires'],
  teleop_command: ['teleop_command'],
  gripper: ['gripper'],
  force_hardware: [],
  video: ['raw_rgb_video', 'insv'],
  depth_image: ['raw_depth_video'],
};

export const BOT_HARDWARE_EQUIPMENT_TYPE_TO_DATA_TYPES: {
  [key in BotHardwareEquipmentType]: {
    label: string;
    dataTypes: Array<{ value: string; label: string }>;
  };
} = {
  robot: {
    label: 'Robot',
    dataTypes: [
      {
        value: 'pose',
        label: 'Pose',
      },
      {
        value: 'joint_states',
        label: 'Joint States',
      },
      {
        value: 'teleop_command',
        label: 'Teleop Command',
      },
      {
        value: 'joint_desires',
        label: 'Joint Desires',
      },
    ],
  },
  camera: {
    label: 'Camera',
    dataTypes: [{ value: 'video', label: 'Video' }],
  },
  depth_sensor: {
    label: 'Depth Sensor',
    dataTypes: [{ value: 'depth_image', label: 'Depth Image' }],
  },
  gripper: {
    label: 'Gripper',
    dataTypes: [{ value: 'gripper', label: 'Gripper' }],
  },
  force_sensor: {
    label: 'Force Sensor',
    dataTypes: [{ value: 'force_hardware', label: 'Force Hardware' }],
  },
};
